import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { X, Download, Upload, Eye, CheckCircle, XCircle, Building } from 'lucide-react';
import classService from '../../../services/classService';
import { getImportErrorMessage } from '../../../lib/import-error';
import { useSchools } from '../hooks/useSchools';
import { CLASS_IMPORT_COLUMNS_HINT, CLASS_IMPORT_FORMATS_HINT, downloadClassTemplate } from '../classImportTemplate';
import ImportRowCells from './ImportRowCells';

interface ImportClassModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess: () => void;
}

interface School {
  id: string;
  name: string;
  code: string;
  type: string;
  level: string;
}

interface PreviewData {
  row: number;
  ten_lop: string;
  nam_hoc: string;
  ten_giao_vien: string;
  ma_giao_vien: string;
  status: string;
}

interface ImportError {
  row: number;
  error: string;
  data: any | null;
}

const ImportClassModal: React.FC<ImportClassModalProps> = ({
  isOpen,
  onClose,
  onImportSuccess
}) => {
  const [step, setStep] = useState<'upload' | 'preview' | 'importing' | 'complete'>('upload');
  const [selectedSchool, setSelectedSchool] = useState<School | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<PreviewData[]>([]);
  const [previewErrors, setPreviewErrors] = useState<ImportError[]>([]);
  const [importErrors, setImportErrors] = useState<ImportError[]>([]);
  const [importedData, setImportedData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Use the useSchools hook to get schools
  const { data: schools = [], isLoading: schoolsLoading, isError: schoolsError, refetch: refetchSchools } = useSchools({
    per_page: 1000
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
      'application/csv': ['.csv']
    },
    multiple: false
  });

  const handleDownloadTemplate = () => downloadClassTemplate('xlsx');
  const handleDownloadCsvTemplate = () => downloadClassTemplate('csv');

  const handlePreview = async () => {
    if (!file || !selectedSchool) return;

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('schoolId', selectedSchool.id);

      const response = await classService.previewImport(formData);
      
      if (response.success) {
        setPreviewData(response.data || []);
        setPreviewErrors(response.errors || []);
        setStep('preview');
      } else {
        setPreviewData([]);
        setPreviewErrors(response.errors || [{
          row: 0,
          error: response.message || 'Có lỗi xảy ra khi xem trước dữ liệu',
          data: null
        }]);
        setStep('preview');
      }
    } catch (error: any) {
      console.error('Preview failed:', error);
      setPreviewData([]);
      setPreviewErrors([{
        row: 0,
        error: getImportErrorMessage(error),
        data: null
      }]);
      setStep('preview');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file || !selectedSchool) return;

    setIsLoading(true);
    setStep('importing');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('schoolId', selectedSchool.id);
      formData.append('schoolName', selectedSchool.name);

      const response = await classService.importClasses(formData);
      
      setImportedData(response.imported || []);
      setImportErrors(response.errors || []);
      setStep('complete');
      
      // Lỗi một phần (207) vẫn đã tạo được một số lớp, nên vẫn làm mới danh sách
      if (response.success || response.imported?.length > 0) {
        onImportSuccess();
      }
    } catch (error: any) {
      console.error('Import failed:', error);
      setImportErrors([{
        row: 0,
        error: getImportErrorMessage(error),
        data: null
      }]);
      setImportedData([]);
      setStep('complete');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedSchool(null);
    setFile(null);
    setPreviewData([]);
    setPreviewErrors([]);
    setImportErrors([]);
    setImportedData([]);
    setStep('upload');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Import lớp học từ Excel/CSV</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {step === 'upload' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Import lớp học từ Excel/CSV
                </h3>
                <p className="text-gray-600">
                  Chọn trường học và tải lên file Excel hoặc CSV để import lớp học
                </p>
                <p className="text-sm text-blue-600 mt-2">
                  💡 {CLASS_IMPORT_COLUMNS_HINT}
                </p>
              </div>

              {/* School Selection */}
              <div className="max-w-md mx-auto">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chọn trường học *
                </label>
                {schoolsLoading ? (
                  <div className="border border-gray-300 rounded-md p-4 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-sm text-gray-600">Đang tải danh sách trường học...</p>
                  </div>
                ) : schoolsError ? (
                  <div className="border border-red-300 rounded-md p-4 text-center bg-red-50">
                    <p className="text-sm text-red-600 mb-2">Lỗi tải danh sách trường học</p>
                    <button
                      onClick={() => refetchSchools()}
                      className="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      Thử lại
                    </button>
                  </div>
                ) : schools.length === 0 ? (
                  <div className="border border-yellow-300 rounded-md p-4 text-center bg-yellow-50">
                    <p className="text-sm text-yellow-600 mb-2">Không tìm thấy trường học nào</p>
                    <button
                      onClick={() => refetchSchools()}
                      className="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      Thử lại
                    </button>
                  </div>
                ) : (
                  <select
                    value={selectedSchool?.id || ''}
                    onChange={(e) => {
                      const school = schools.find(s => s.id === e.target.value);
                      setSelectedSchool(school || null);
                    }}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Chọn trường học --</option>
                    {schools.map((school) => (
                      <option key={school.id} value={school.id}>
                        {school.name} ({school.code})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Selected School Info */}
              {selectedSchool && (
                <div className="max-w-md mx-auto">
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <div className="flex items-center">
                      <Building className="h-5 w-5 text-blue-600 mr-2" />
                      <div>
                        <h4 className="text-sm font-medium text-blue-900">{selectedSchool.name}</h4>
                        <p className="text-sm text-blue-700">
                          Mã: {selectedSchool.code} | Loại: {selectedSchool.type} | Cấp: {selectedSchool.level}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* File Upload Section - Only show when school is selected */}
              {selectedSchool && (
                <>
                  {/* Download Template */}
                  <div className="text-center space-y-2">
                    <p className="text-sm text-gray-600 mb-3">Tải file mẫu:</p>
                    <div className="flex justify-center space-x-3">
                      <button
                        onClick={handleDownloadTemplate}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Excel (.xlsx)
                      </button>
                      <button
                        onClick={handleDownloadCsvTemplate}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        CSV (.csv)
                      </button>
                    </div>
                  </div>

                  {/* File Upload */}
                  <div
                    {...getRootProps()}
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                      isDragActive
                        ? 'border-blue-400 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <input {...getInputProps()} />
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    {file ? (
                      <div>
                        <p className="text-sm text-gray-600">File đã chọn:</p>
                        <p className="text-sm font-medium text-gray-900">{file.name}</p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm text-gray-600">
                          {isDragActive
                            ? 'Thả file vào đây...'
                            : 'Kéo thả file Excel/CSV vào đây hoặc click để chọn file'}
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          {CLASS_IMPORT_FORMATS_HINT}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Preview Button */}
                  {file && (
                    <div className="text-center">
                      <button
                        onClick={handlePreview}
                        disabled={isLoading}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        {isLoading ? 'Đang xử lý...' : 'Xem trước'}
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* Message when no school selected */}
              {!selectedSchool && (
                <div className="text-center py-8">
                  <p className="text-gray-500">Vui lòng chọn trường học để tiếp tục</p>
                </div>
              )}
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Xem trước dữ liệu import
                </h3>
                <p className="text-gray-600">
                  Kiểm tra dữ liệu trước khi import
                </p>
                {selectedSchool && (
                  <p className="text-sm text-green-600 mt-1">
                    📍 Trường học: {selectedSchool.name}
                  </p>
                )}
              </div>

              {/* Preview Table */}
              <div className="overflow-y-auto max-h-96">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Dòng
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tên lớp
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Năm học
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tên giáo viên
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Mã giáo viên
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Trạng thái
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {previewData.map((row) => (
                        <tr key={row.row} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {row.row}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {row.ten_lop}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {row.nam_hoc}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {row.ten_giao_vien}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {row.ma_giao_vien}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Hợp lệ
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Errors */}
              {previewErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <h4 className="text-sm font-medium text-red-800 mb-2">
                    Có {previewErrors.length} lỗi cần sửa:
                  </h4>
                  <div className="space-y-3">
                    {previewErrors.map((error, index) => (
                      <div key={index} className="bg-white border border-red-200 rounded-md p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-red-800 mb-1">
                              {error.row > 0 && `Dòng ${error.row}: `}{error.error}
                            </p>
                            <ImportRowCells data={error.data} />
                          </div>
                          <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 ml-2" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-between">
                <button
                  onClick={() => setStep('upload')}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Quay lại
                </button>
                <button
                  onClick={handleImport}
                  disabled={previewErrors.length > 0 || isLoading}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {isLoading ? 'Đang import...' : 'Import'}
                </button>
              </div>
            </div>
          )}

          {step === 'importing' && (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Đang import dữ liệu...
              </h3>
              <p className="text-gray-600">
                Vui lòng đợi trong khi hệ thống xử lý file
              </p>
            </div>
          )}

          {step === 'complete' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Import hoàn tất
                </h3>
                <p className="text-gray-600">
                  {importedData.length} lớp học đã được import thành công
                </p>
                {selectedSchool && (
                  <p className="text-sm text-green-600 mt-1">
                    📍 Trường học: {selectedSchool.name}
                  </p>
                )}
              </div>

              {/* Scrollable Content Area */}
              <div className="overflow-y-auto max-h-96">
                {/* Success Summary */}
                {importedData.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
                    <h4 className="text-sm font-medium text-green-800 mb-2">
                      Import thành công:
                    </h4>
                    <ul className="text-sm text-green-700 space-y-1">
                      {importedData.map((item, index) => (
                        <li key={index}>
                          Dòng {item.row}: {item.class?.name}{item.teacher?.name ? ` - ${item.teacher.name}` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Error Summary */}
                {importErrors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <h4 className="text-sm font-medium text-red-800 mb-2">
                      Có {importErrors.length} lỗi:
                    </h4>
                    <div className="space-y-3">
                      {importErrors.map((error, index) => (
                        <div key={index} className="bg-white border border-red-200 rounded-md p-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-red-800 mb-1">
                                {error.row > 0 && `Dòng ${error.row}: `}{error.error}
                              </p>
                              <ImportRowCells data={error.data} />
                            </div>
                            <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 ml-2" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-center space-x-4">
                <button
                  onClick={handleReset}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Import file khác
                </button>
                <button
                  onClick={handleClose}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  Hoàn tất
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportClassModal;
